import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, User, Building, MessageSquare, ArrowRight, ChevronDown } from 'lucide-react';
import emailjs from "@emailjs/browser";



export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    caseType: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);



  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.caseType.trim() || !formData.subject.trim() || !formData.message.trim()) {
      setStatus({ type: 'error', message: 'Please fill all required fields.' });
      return;
    }



    setIsSubmitting(true);
    setStatus({ type: '', message: '' });



    try {
      // ✅ Send ONE email to admin (blackthornchambers@gmail.com) with CC to your personal email
      await emailjs.send(
        "service_2aewgq9",
        "template_4cre0yk",
        {
          to_email: "blackthornchambers@gmail.com",
          cc_email: "shivadityasharma3116@gmail.com",
          name: formData.name,
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone || "Not provided",
          caseType: formData.caseType,
          subject: formData.subject,
          message: formData.message,
          contact_message: `NAME: ${formData.name}
EMAIL: ${formData.email}
PHONE: ${formData.phone || "N/A"}
CASE TYPE: ${formData.caseType}
SUBJECT: ${formData.subject}



MESSAGE:
${formData.message}`
        },
        "0efh29XDRVfrvpH7a"
      );



      setStatus({
        type: 'success',
        message: '✅ Message sent successfully! We will get back to you soon.'
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', caseType: '' });



    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus({ type: 'error', message: 'Failed to send. Please try WhatsApp or call us.' });
    } finally {
      setIsSubmitting(false);
    }
  };



  const scrollToForm = () => {
    const element = document.getElementById('contact-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };



  const openMapLocation = (e) => {
    e.preventDefault();
    
    const latitude = 28.569588;
    const longitude = 77.323109;
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.location.href = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
    }
  };



  const contactInfo = [
    { icon: Clock, title: 'Working Hours', details: ['Mon - Sat: 9:30 AM - 6:30 PM'], link: null, onClick: null },
    { icon: Phone, title: 'Phone', details: ['+91 73032 69472'], link: 'tel:+917303269472',onClick: null },
    { icon: Mail, title: 'Email', details: ['blackthornchambers@gmail.com'], link: 'mailto:blackthornchambers@gmail.com',onClick: null },
    { icon: MapPin, title: 'Office', details: ['Wave Mall, Sector 18, Noida'], link: '#', onClick: openMapLocation },
  ];



  const caseTypes = [
    "Corporate & Commercial Laws",
    "Mergers, Acquisitions & Joint Ventures",
    "Corporate Governance & Compliance",
    "Startup Advisory & Business Structuring",
    "Civil Litigation & Commercial Disputes",
    "Criminal Law & Defense",
    "White-Collar Crimes & Economic Offences",
    "Enforcement Directorate (ED) Matters",
    "Central Bureau of Investigation (CBI) Matters",
    "Arbitration, Mediation & Alternative Dispute Resolution (ADR)",
    "International & Cross-Border Disputes",
    "Banking & Finance Laws",
    "Insolvency & Bankruptcy Code (IBC) Matters",
    "Project Finance & Infrastructure Laws",
    "Goods and Services Tax (GST) Matters",
    "Income Tax & Direct Tax Matters",
    "Customs, Excise & International Taxation",
    "Real Estate & Property Laws",
    "Land Acquisition & Title Due Diligence",
    "Labour & Employment Laws",
    "Service Law & Government Employment Matters",
    "Family & Matrimonial Laws",
    "Succession, Inheritance & Estate Planning",
    "Intellectual Property Rights (IPR)",
    "Information Technology & Cyber Laws",
    "Data Protection & Privacy Laws",
    "Consumer Protection Laws",
    "Constitutional & Administrative Law",
    "Environmental Laws",
    "Regulatory & Compliance Services",
    "Legal Retainership & Ongoing Advisory",
    "Notarial & Documentation Services",
    "Trust, Society & NGO Laws",
    "Media, Entertainment & Sports Laws",
    "Insurance & Medical Negligence Laws",
    "Election & Political Law Advisory",
    "Other (Please Specify)"
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* HERO SECTION WITH CTA */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full mb-6">
            <MessageSquare className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-xs sm:text-sm font-semibold tracking-widest uppercase">
              Get In Touch
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Let's Discuss Your <span className="text-yellow-400">Legal Needs</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8 sm:mb-10">
            Schedule a consultation with our experienced legal professionals. We're here to help you navigate your legal matters with expertise and care.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <button
              onClick={scrollToForm}
              className="w-full sm:w-auto px-8 py-4 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 min-w-[200px]"
            >
              <MessageSquare className="w-5 h-5" />
              <span>Fill Contact Form</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <a
              href="tel:+917303269472"
              className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/50 text-white font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 min-w-[200px] backdrop-blur-sm"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </a>



            <a
              href="https://wa.me/917303269472?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20legal%20services"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-green-500 hover:bg-green-600 border-2 border-green-400 hover:border-green-300 text-white font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2 min-w-[200px]"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>



      {/* CONTACT INFO CARDS - Hidden on mobile, visible on desktop */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10 hidden lg:block">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {contactInfo.map((info, idx) => {
            const Icon = info.icon;
            const CardWrapper = info.link && info.link !== '#' ? 'a' : 'div';
            
            return (
              <CardWrapper
                key={idx}
                href={info.link && info.link !== '#' ? info.link : undefined}
                onClick={info.onClick ? info.onClick : undefined}
                className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-900 rounded-lg mb-4">
                  <Icon className="w-6 h-6 text-yellow-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-sm font-medium text-slate-600 mb-1">
                    {detail}
                  </p>
                ))}
              </CardWrapper>
            );
          })}
        </div>
      </section>



      {/* MAIN CONTENT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          <div id="contact-form" className="lg:col-span-3 order-1 lg:order-1 scroll-mt-6">
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-6">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>



              {status.message && (
                <div className={`flex items-start gap-3 p-4 rounded-lg mb-6 ${status.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {status.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm ${status.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                    {status.message}
                  </p>
                </div>
              )}



              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all" placeholder="Enter your full name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all" placeholder="your.email@example.com" />
                    </div>
                  </div>
                </div>



                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all" placeholder="+91 98765 XXXXX" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Case Type *</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
                      <select 
                        name="caseType" 
                        value={formData.caseType} 
                        onChange={handleChange} 
                        required 
                        className="w-full pl-11 pr-10 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Select case type</option>
                        {caseTypes.map((type, idx) => (
                          <option key={idx} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>



                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Subject *</label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all" placeholder="Brief description of your legal matter" />
                </div>



                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows="6" className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition-all resize-none" placeholder="Please provide details about your case..."></textarea>
                </div>



                <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>



          {/* Sidebar remains unchanged */}
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-2">
            <div className="lg:hidden space-y-4">
              <a href="tel:+917303269472" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-900 rounded-lg flex-shrink-0">
                  <Phone className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Phone</h3>
                  <p className="text-sm font-medium text-slate-600">+91 73032 69472</p>
                </div>
              </a>



              <a href="https://wa.me/917303269472?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20legal%20services" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-lg flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">WhatsApp</h3>
                  <p className="text-sm font-medium text-slate-600">Chat with us</p>
                </div>
              </a>
              
              <a href="mailto:blackthornchambers@gmail.com" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-all">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-900 rounded-lg flex-shrink-0">
                  <Mail className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Email</h3>
                  <p className="text-sm font-medium text-slate-600">blackthornchambers@gmail.com</p>
                </div>
              </a>
              
              <a
                href="#"
                onClick={openMapLocation}
                className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-all"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-900 rounded-lg flex-shrink-0">
                  <MapPin className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Office</h3>
                  <p className="text-sm font-medium text-slate-600">Wave Mall, Sector 18, Noida</p>
                </div>
              </a>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="h-64 bg-slate-200 relative cursor-pointer group" onClick={openMapLocation}>
                <div className="absolute inset-0 z-10 bg-transparent hover:bg-black/5 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900/90 text-white px-6 py-3 rounded-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span className="font-semibold">Open in Google Maps</span>
                  </div>
                </div>
                
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.550739230032!2d77.32006907549808!3d28.568236775699253!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5fae41d6d0b%3A0x9d8f92487b33d8d9!2sWave%20One%2C%20Sector%2018%2C%20Noida!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Office Location"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Visit Our Office</h3>
                <p className="text-sm font-medium text-slate-600">
                  Wave One, Sector 18, Noida, Uttar Pradesh - 201301, India
                </p>
                <button
                  onClick={openMapLocation}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-yellow-600 hover:text-yellow-700 transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  <span>View on Google Maps</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>



            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-4">Need Immediate Assistance?</h3>
              <p className="text-slate-300 text-sm mb-6">
                For urgent legal matters, please call us directly or visit our office during working hours.
              </p>
              <div className="space-y-3">
                <a href="tel:+917303269472" className="flex items-center gap-3 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                  <Phone className="w-5 h-5 text-yellow-400" />
                  <div>
                    <div className="text-xs text-slate-400">Call Us</div>
                    <div className="font-medium">+91 73032 69472</div>
                  </div>
                </a>



                <a href="https://wa.me/917303269472?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20legal%20services" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <div>
                    <div className="text-xs text-slate-400">WhatsApp</div>
                    <div className="font-medium">Chat with us</div>
                  </div>
                </a>



                <a href="mailto:blackthornchambers@gmail.com" className="flex items-center gap-3 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <div>
                    <div className="text-xs text-slate-400">Email Us</div>
                    <div className="font-medium">blackthornchambers@gmail.com</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
